import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { assignAppointmentProvider, newIdempotencyKey } from "../services/bookingService";
import { fetchProviders } from "../services/providerService";
import { formatTime } from "../services/datetime";

/**
 * Assign / change the provider of ONE service of a booking.
 *
 * Reschedule moves every line onto one person; this changes just the line
 * chosen, at its current time. The list is the eligible set for the line's
 * branch and service (as in RescheduleModal); the server still decides
 * whether that person is free for the line's whole window and answers 409
 * when not, which is shown here rather than silently retried.
 */
export default function AssignProviderModal({ open, appointment, line, onClose, onAssigned }) {
  const { t } = useTranslation();

  const [providers, setProviders] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newIdempotencyKey);

  useEffect(() => {
    if (!open || !appointment || !line) return;

    setStaffId(line.staff_id ?? "");
    setError("");
    setKey(newIdempotencyKey());
    setLoading(true);

    fetchProviders({
      perPage: 100,
      filters: {
        branch: appointment.branch_id,
        service: line.service_id,
        employmentStatus: "active",
      },
    })
      .then(({ providers: list }) => setProviders(list))
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, [open, appointment, line]);

  if (!open || !appointment || !line) return null;

  const unchanged = !staffId || staffId === (line.staff_id ?? "");

  const submit = async (event) => {
    event.preventDefault();
    if (unchanged) return;

    setSaving(true);
    setError("");

    try {
      const updated = await assignAppointmentProvider(appointment.id, line.id, staffId, key);
      onAssigned?.(updated);
      onClose();
    } catch (err) {
      // A refused write is settled: the next try is a new operation.
      setKey(newIdempotencyKey());
      setError(
        apiErrorMessage(
          err,
          err.response?.status === 409 ? t("appointments.assign.notFree") : t("appointments.assign.failed")
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-md p-5">
        <h2 className="mb-1 text-lg font-bold">
          {line.staff ? t("appointments.assign.changeTitle") : t("appointments.assign.title")}
        </h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("appointments.assign.subtitle", {
            service: line.service?.name ?? "",
            time: formatTime(line.local_starts_at),
          })}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="field-label" htmlFor="assign-staff">
              {t("appointments.provider")}
            </label>
            <select
              id="assign-staff"
              className="field-input"
              value={staffId}
              disabled={loading}
              onChange={(e) => setStaffId(e.target.value)}
            >
              <option value="">{t("appointments.assign.choose")}</option>
              {/* The current provider stays listed even if no longer eligible,
                  so the select shows who it is now. */}
              {line.staff && !providers.some((p) => p.id === line.staff.id) && (
                <option value={line.staff.id}>{line.staff.display_name}</option>
              )}
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.display_name}
                </option>
              ))}
            </select>
            {!loading && providers.length === 0 && (
              <p className="mt-1 text-xs text-warning-text">{t("appointments.assign.noneEligible")}</p>
            )}
            <p className="mt-1 text-xs text-ink-2">{t("appointments.assign.hint")}</p>
          </div>

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || unchanged}>
              {saving ? t("common.saving") : t("appointments.assign.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
